import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Switch, Button, Modal, Form, Input, Row, Col, Tooltip } from 'antd'
import { EditOutlined, FormOutlined, HistoryOutlined } from '@ant-design/icons'
import _ from 'lodash'
import { isObject, safeObjectToString } from '@/common'
import FormLayout from 'components/FormLayout'
import ManageUsers, { ManageUsersContext } from 'components/ManageUsers'

export default function HotelUsers() {
    return (
        <ManageUsers product="hotel" resource="hotel-user">
            <InternalHotelUsers />
        </ManageUsers>
    )
}

function InternalHotelUsers() {
    const { product, canEdit, onRegisterColumns, onOpenEditForm, onSelectUser, dispatchEvent } = useContext(
        ManageUsersContext
    )

    const onShowAdjustCugConfirm = async record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.cug ? 'off' : 'on'} cug`,
            onOk: async () => {
                dispatchEvent('cug', record)
            }
        })
    }

    const onShowConvertCurrencyConfirm = record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.convert_currency ? 'off' : 'on'} convert currency`,
            onOk: async () => {
                dispatchEvent('currency', record)
            }
        })
    }

    const onShowChangeStatusConfirm = record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.active ? 'off' : 'on'} ${record.email}`,
            onOk: async () => {
                dispatchEvent('status', record)
            }
        })
    }

    const columns = [
        {
            key: 'name',
            dataIndex: 'name',
            title: 'User',
            ellipsis: true
        },
        {
            key: 'email',
            dataIndex: 'email',
            title: 'Email',
            ellipsis: true
        },
        {
            key: 'cug',
            dataIndex: 'cug',
            title: 'CUG',
            render: (_, record) => (
                <Switch disabled={!canEdit} checked={record.cug} onClick={() => onShowAdjustCugConfirm(record)} />
            )
        },
        {
            key: 'convert_currency',
            dataIndex: 'convert_currency',
            title: 'Convert Currency',
            render: (_, record) => (
                <Switch
                    disabled={!canEdit}
                    checked={record.convert_currency}
                    onClick={() => onShowConvertCurrencyConfirm(record)}
                />
            )
        },
        {
            key: 'status',
            dataIndex: 'active',
            title: 'Active',
            render: (_, record) => (
                <Switch
                    disabled={!canEdit}
                    checked={record.active}
                    onChange={() => onShowChangeStatusConfirm(record)}
                />
            )
        },
        {
            key: 'action',
            title: 'Action',
            render: (_, record) => (
                <Row gutter={[8, 8]}>
                    <Col sm={24} md={8}>
                        <Tooltip title="Edit">
                            <Button
                                ghost
                                type="primary"
                                icon={<EditOutlined />}
                                disabled={!canEdit}
                                onClick={() => onOpenEditForm(record)}
                            />
                        </Tooltip>
                    </Col>
                    <Col sm={24} md={8}>
                        <Link to={`/${product}/users/${record.id}`}>
                            <Button ghost type="danger" icon={<FormOutlined />} disabled={!canEdit} />
                        </Link>
                    </Col>
                    <Col sm={24} md={8}>
                        <Tooltip title="Show Log">
                            <Button type="dashed" icon={<HistoryOutlined />} onClick={() => onSelectUser(record)} />
                        </Tooltip>
                    </Col>
                </Row>
            )
        }
    ]

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => onRegisterColumns(columns), [])

    return (
        <React.Fragment>
            <HotelUserForm />
        </React.Fragment>
    )
}

function HotelUserForm() {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [isConvertCurrency, setIsConvertCurrency] = useState(false)
    const { formState, onHideForm, onSave, onReload } = useContext(ManageUsersContext)

    const fields = [
        'name',
        'email',
        'password',
        'extra_config',
        'cug',
        'active',
        'convert_currency',
        'default_currency'
    ]

    useEffect(() => {
        form.resetFields()

        if (formState.action === 'edit') {
            const values = _.pick(formState.payload, fields)
            values.extra_config = safeObjectToString(values.extra_config)
            form.setFieldsValue(values)
        }

        if (formState.action === 'create') {
            form.setFieldsValue({
                active: true,
                cug: false,
                convert_currency: false
            })
        }
    }, [formState])

    const onSubmit = () => {
        form.validateFields().then(fieldsValue => {
            setLoading(true)

            onSave(fieldsValue)
                .then(() => {
                    setLoading(false)
                    onHideForm()
                    onReload()
                })
                .catch(() => {
                    setLoading(false)
                })
        })
    }

    const onPaste = () => {}

    return (
        <FormLayout
            type="user"
            action={formState.action}
            open={formState.action === 'create' || formState.action === 'edit'}
            loading={loading}
            onCloseForm={onHideForm}
            onSubmit={onSubmit}
            onPaste={onPaste}>
            <Form form={form} colon={false} layout="vertical">
                <Form.Item
                    label="Name"
                    name="name"
                    validateTrigger="onBlur"
                    hasFeedback
                    required
                    rules={[
                        {
                            required: true,
                            message: 'Name is required'
                        }
                    ]}>
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Email"
                    name="email"
                    validateTrigger="onBlur"
                    hasFeedback
                    required
                    rules={[
                        {
                            required: true,
                            message: 'Email is required'
                        },
                        {
                            type: 'email',
                            message: 'Invalid email'
                        }
                    ]}>
                    <Input />
                </Form.Item>

                <Form.Item
                    label="Password"
                    name="password"
                    validateTrigger="onBlur"
                    hasFeedback
                    required
                    rules={[
                        {
                            required: true,
                            message: 'Password is required'
                        }
                    ]}>
                    <Input.Password />
                </Form.Item>

                <Form.Item label="CUG" name="cug" valuePropName="checked">
                    <Switch />
                </Form.Item>

                <Form.Item label="Active" name="active" valuePropName="checked">
                    <Switch />
                </Form.Item>

                <Form.Item label="Convert Currency" name="convert_currency" valuePropName="checked">
                    <Switch onChange={checked => setIsConvertCurrency(checked)} />
                </Form.Item>
                {isConvertCurrency && (
                    <Form.Item
                        label="Default Currency"
                        name="default_currency"
                        validateTrigger="onBlur"
                        hasFeedback
                        rules={[
                            {
                                required: true,
                                message: 'Currency code must be 3 alphabet character'
                            }
                        ]}>
                        <Input minLength={3} maxLength={3} />
                    </Form.Item>
                )}

                <Form.Item
                    label="Extra Config"
                    name="extra_config"
                    validateTrigger="onBlur"
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value && !isObject(value)) {
                                    return Promise.reject('Invalid object')
                                }

                                return Promise.resolve()
                            }
                        }
                    ]}>
                    <Input.TextArea
                        row={6}
                        onChange={e =>
                            form.setFieldsValue({
                                extra_config: !e.target.value ? null : e.target.value
                            })
                        }
                    />
                </Form.Item>
            </Form>
        </FormLayout>
    )
}
